import { atom, type Atom, type PrimitiveAtom } from 'jotai';
import { type IEnvironment } from 'relay-runtime';

import { withRequestTiming } from '../../common/analytics/withRequestTiming';
import { logMessage } from '../../common/sentry';
import { requestKnowledgeSources } from '../../utils/api/appConnectors';
import { requestAvailableProducts } from '../../utils/api/availableProducts';
import {
	getRecommendationDropReason,
	getRecommendationWarning,
	requestCollaborationUsers,
	type CollabGraphRecommendedEntity,
} from '../../utils/api/collaborationUsers';
import { requestGlobalMetrics, type GlobalMetricsResponse } from '../../utils/api/globalMetrics';
import {
	extractPermittedCloudIds,
	isJsmOnlySite,
	requestJsmSitePermissions,
} from '../../utils/api/jsmSitePermissions';
import {
	requestOrgMetrics,
	type NoDataResponse,
	type OrgMetricsResponse,
} from '../../utils/api/orgMetrics';
import {
	requestUserMetrics,
	isUserNoDataResponse,
	type UserMicrositeHeroMetricsResponse,
} from '../../utils/api/userMetrics';
import {
	STATIC_COLLABORATION_USERS,
	STATIC_TIER2_DATASETS,
	STATIC_TIER1_DATASETS,
	STATIC_TIER3_DATASETS,
	type DebugTier,
	type StaticTier2DatasetKey,
	type StaticTier1DatasetKey,
	type StaticTier3DatasetKey,
	TIER_3_USER_DATASET,
} from '../components/DebugPanel/staticDatasets';
import type { AvailableSite } from '../components/SiteSwitcher/types';
import { SiteSelectionSource } from '../components/SiteSwitcher/types';

export type GettingStartedTab = 'cli' | 'mcp';

/**
 * Debug toggle to preview the logged-out view while authenticated.
 * When true, Home renders LoggedOutView instead of LoggedInView.
 * Only intended for development/preview purposes.
 */
export const forceLoggedOutViewAtom: PrimitiveAtom<boolean> & {
	init: boolean;
} = atom<boolean>(false);

/**
 * Debug toggle to preview the benchmark waitlist view on `/benchmark` even
 * when the cloud benchmark gate would render the benchmark itself.
 * Only intended for development/preview purposes.
 */
export const forceBenchmarkWaitlistViewAtom: PrimitiveAtom<boolean> & {
	init: boolean;
} = atom<boolean>(false);

/**
 * Selects how `OrgMetricsAtom` sources its payload.
 *
 * - `'api'` (default): fetch from the live upstream endpoints (e.g.
 *   `metrics/org/{orgId}` for `OrgMetricsAtom`, `recommend/user` for
 *   `CollaborationUsersAtom`) using the live tenant atoms.
 * - `'static'`: short-circuit the network call and serve the canned
 *   payload bundled with the debug panel. For `OrgMetricsAtom` this is
 *   the dataset identified by `selectedStaticTier2DatasetKeyAtom`; for
 *   `CollaborationUsersAtom` it is the shared `STATIC_COLLABORATION_USERS`
 *   roster (the same five users for every dataset).
 *
 * Only intended for development/preview purposes via the debug panel.
 */
export type OrgMetricsSource = 'api' | 'static';

/**
 * Writable atom controlling which source the debug-panel-aware atoms
 * (`OrgMetricsAtom`, `CollaborationUsersAtom`) read from. Defaults to
 * `'api'` so the production logged-in flow is unchanged when the debug
 * panel has not been touched.
 */
export const orgMetricsSourceAtom: PrimitiveAtom<OrgMetricsSource> & {
	init: OrgMetricsSource;
} = atom<OrgMetricsSource>('api');

/**
 * Writable atom holding which debug-panel tier is currently active.
 * Selecting a tier in the debug panel forces `LoggedInView` to render
 * the corresponding view (Tier 1 → `UserGraphView`, Tier 2 →
 * `OrgGraphView`, Tier 3 → `GettingStartedView`) regardless of the
 * live tier-gating logic.
 *
 * Defaults to `'tier2'` so existing static datasets continue to route
 * to `OrgGraphView` when the debug panel has not been touched.
 */
export const selectedDebugTierAtom: PrimitiveAtom<DebugTier> & {
	init: DebugTier;
} = atom<DebugTier>('tier1');

/**
 * Writable atom holding the tier currently being rendered by
 * `LoggedInView` — `'tier1'` for `UserGraphView`, `'tier2'` for
 * `OrgGraphView`, `'tier3'` for `GettingStartedView`, or `null` before
 * the gating logic has run.
 */
export const currentDisplayedTierAtom: PrimitiveAtom<DebugTier | null> & {
	init: DebugTier | null;
} = atom<DebugTier | null>(null);

/**
 * Writable atom holding the currently selected static *Tier 1* dataset
 * key for the debug panel. `null` means "no Tier 1 dataset chosen yet".
 * When `orgMetricsSourceAtom` is `'static'`, `selectedDebugTierAtom` is
 * `'tier1'`, and this is non-null, `UserMetricsAtom` resolves
 * synchronously to the matching `UserMicrositeHeroMetricsResponse`
 * payload from `STATIC_TIER1_DATASETS`.
 */
export const selectedStaticTier1DatasetKeyAtom: PrimitiveAtom<StaticTier1DatasetKey | null> & {
	init: StaticTier1DatasetKey | null;
} = atom<StaticTier1DatasetKey | null>(null);

/**
 * Writable atom holding the currently selected static *Tier 2* dataset
 * key for the debug panel. `null` means "no Tier 2 dataset chosen yet".
 * When `orgMetricsSourceAtom` is `'static'`, `selectedDebugTierAtom` is
 * `'tier2'`, and this is non-null:
 * - `OrgMetricsAtom` resolves synchronously to the matching
 *   `OrgMetricsResponse` payload from `STATIC_TIER2_DATASETS`.
 * - `CollaborationUsersAtom` resolves synchronously to the shared
 *   `STATIC_COLLABORATION_USERS` roster (the same roster regardless of
 *   which dataset key is selected).
 */
export const selectedStaticTier2DatasetKeyAtom: PrimitiveAtom<StaticTier2DatasetKey | null> & {
	init: StaticTier2DatasetKey | null;
} = atom<StaticTier2DatasetKey | null>(null);

/**
 * Writable atom holding the currently selected static *Tier 3* dataset
 * key for the debug panel. `null` means "no Tier 3 dataset chosen yet".
 * Tier 3 datasets are label/description pairs only — the capability
 * showcase view does not read numeric data from them — so this atom
 * exists purely to gate whether the debug panel has forced a Tier 3
 * preview and to remember the user's selection across re-renders.
 */
export const selectedStaticTier3DatasetKeyAtom: PrimitiveAtom<StaticTier3DatasetKey | null> & {
	init: StaticTier3DatasetKey | null;
} = atom<StaticTier3DatasetKey | null>(null);

/**
 * Writable atom that holds the current cloud ID.
 *
 * Initialised to `null` to represent the "not yet known" state. The real
 * cloud ID is hydrated from the response of the `/gateway/api/app-switcher`
 * `available-products` endpoint (see the `availableSitesAtom` /
 * `LoggedInGate` flow). Async atoms that depend on this value (e.g.
 * `KnowledgeSourcesAtom`, `CollaborationUsersAtom`) early-return an empty
 * idle result while it is `null`, so no network calls are made with an
 * unknown tenant.
 *
 * This atom can be updated at runtime — for example, when the user
 * switches tenants via the `SiteSwitcher` — by calling `set` on it.
 */
export const cloudIdAtom: PrimitiveAtom<string | null> & {
	init: string | null;
} = atom<string | null>(null);

/**
 * Display name of the site `cloudIdAtom` points at, or `null` while none is
 * resolved.
 *
 * Written wherever `cloudIdAtom` is, so the two never disagree. It exists so a
 * consumer can name the current tenant without reading `availableSitesAtom`,
 * which suspends and would push a Suspense boundary onto every such consumer.
 */
export const selectedSiteNameAtom: PrimitiveAtom<string | null> & {
	init: string | null;
} = atom<string | null>(null);

/**
 * Hostname of the site `cloudIdAtom` points at, for example
 * `hello.atlassian.net`, or `null` while none is resolved.
 *
 * Held alongside `selectedSiteNameAtom` rather than derived from it: the
 * display name is customer-chosen prose and need not resemble the address.
 * This is the value to show wherever the tenant has to be identified exactly
 * rather than merely named.
 */
export const selectedSiteHostnameAtom: PrimitiveAtom<string | null> & {
	init: string | null;
} = atom<string | null>(null);

/**
 * Writable atom that holds the current org ID.
 *
 * The initial value is derived from `getConfig().orgId`, which
 * resolves based on the current environment (staging, production, etc.).
 *
 * This atom can be updated at runtime — for example, when the user
 * switches tenants via an app-switcher — by calling `set` on it.
 */
export const orgIdAtom: PrimitiveAtom<string | null> & {
	init: string | null;
} = atom<string | null>(null);

export const previousSelectionSourceAtom: PrimitiveAtom<SiteSelectionSource> & {
	init: SiteSelectionSource;
} = atom<SiteSelectionSource>(SiteSelectionSource.Default);

/**
 * Writable atom that holds the current user's Atlassian account ID.
 *
 * Initialised as an empty string and populated by `LoggedInView` once
 * the `UserProvider` has resolved the authenticated user. Modelled as
 * a Jotai atom (rather than read directly from React context) so that
 * async atoms which depend on the account ID — such as
 * `CollaborationUsersAtom` — can be evaluated alongside other data
 * fetching atoms (e.g. `KnowledgeSourcesAtom`) without needing to be
 * wired through React state.
 */
export const accountIdAtom: PrimitiveAtom<string> & {
	init: string;
} = atom<string>('');

/**
 * Holds the active Relay `IEnvironment` so async atoms can issue GraphQL
 * queries without needing to live inside a React component.
 *
 * Hydrated once by `LoggedInView` via `useHydrateAtoms` from the
 * environment provided by `@atlassian/relay-environment-provider`. Atoms
 * that depend on this should treat a `null` value as "not ready yet" and
 * return an empty/idle result instead of firing a network request.
 */
export const relayEnvironmentAtom: PrimitiveAtom<IEnvironment | null> & {
	init: IEnvironment | null;
} = atom<IEnvironment | null>(null);

/**
 * Result type returned by `availableSitesAtom`.
 *
 * On success, `data` is the list of sites the user can access (used by
 * `SiteSwitcher` for rendering and by `LoggedInGate` for resolving the
 * initial `cloudId`). On failure, `data` is empty and `error` carries
 * the message; consumers can fall back gracefully without crashing.
 */
export type AvailableSitesResult = {
	data: AvailableSite[];
	error: string | null;
};

/**
 * Derived async atom that fetches the list of Atlassian sites/products
 * available to the current user via the App Switcher gateway endpoint.
 *
 * This atom is intentionally evaluated *before* `LoggedInView` mounts,
 * so the `LoggedInGate` wrapper can pick the correct `cloudId` from the
 * response and hydrate `cloudIdAtom` with it. Downstream atoms that
 * depend on `cloudIdAtom` (e.g. `KnowledgeSourcesAtom`,
 * `CollaborationUsersAtom`) early-return an idle result while
 * `cloudIdAtom` is `null`, ensuring no network calls are made with an
 * unknown tenant.
 *
 * This atom additionally hides JSM-only sites where the user is a
 * non-admin and a follow-up `permissions/bulk/permitted` check returns
 * `permitted: false` for `write` on the JSM site resource. The
 * permission call is only issued when at least one candidate site
 * exists; on any error the candidates are kept (fail open).
 *
 * Note on critical-path latency: this introduces a deliberate
 * sequential waterfall (available-products → bulk-permitted)
 * for the small subset of users who have at least one non-admin
 * JSM-only site. This is intentional and required for correctness — we
 * must not render the Site Switcher with sites the user is not
 * permitted to use, even transiently, so the filter has to complete
 * before any consumer reads this atom. The waterfall cannot be
 * parallelised because the second request's `resourceId` list is
 * derived from the first response's `cloudId` + `availableProducts` +
 * `adminAccess` fields. Optimisations considered and rejected:
 *   - Issuing the permission call speculatively for every non-admin
 *     site in parallel — over-fetches on every page load for the common
 *     case (users with no JSM-only sites) and net-regresses TTFP.
 *   - Deferring the filter to a separate, lazily-evaluated atom that
 *     the Site Switcher reads — would briefly leak unauthorised sites
 *     into the dropdown before the permission check resolves, which is
 *     the exact bug GRAPHYTE-2729 is fixing.
 * The added cost is bounded by `candidateCloudIds.length` (only sites
 * that pass the JSM-only + non-admin gates), and the second call is
 * skipped entirely when no candidates exist.
 *
 * Never throws — on failure, returns `{ data: [], error: "..." }` so
 * consumers can fall back gracefully.
 *
 * Components using this atom will suspend (React Suspense) until the
 * data is resolved. Wrap the consuming subtree in a
 * `<Suspense fallback={...}>` boundary.
 */
export const availableSitesAtom: Atom<Promise<AvailableSitesResult>> = atom<
	Promise<AvailableSitesResult>
>(async () => {
	try {
		const { execute } = requestAvailableProducts();
		const response = await withRequestTiming(
			'operational.availableProductsRequest.completed.twgMicrositeAvailableProducts',
			execute,
		);
		if (!response.success) {
			return {
				data: [],
				error: `Failed to fetch available products: ${response.error?.message ?? response.error}`,
			};
		}
		const sites = response.data.sites.filter((site) => site?.org !== undefined);

		// Identify non-admin sites whose only "core productivity" product is
		// JSM. Each such site is hidden from the switcher unless the bulk
		// permission check confirms the user has `write` access on the JSM
		// resource. Admin sites are kept unconditionally; sites with another
		// core product (e.g. JIRA_SOFTWARE, CONFLUENCE) are kept regardless
		// because the user has another reason to see them in the switcher.
		const candidateCloudIds = sites
			.filter(
				(site) =>
					site.adminAccess === false && typeof site.cloudId === 'string' && isJsmOnlySite(site),
			)
			.map((site) => site.cloudId)
			.filter((cloudId) => cloudId !== undefined);

		if (candidateCloudIds.length === 0) {
			return { data: sites, error: null };
		}

		try {
			const permResponse = await requestJsmSitePermissions(candidateCloudIds);
			if (!permResponse.success) {
				return { data: sites, error: null }; // Fail open
			}
			const permittedCloudIds = extractPermittedCloudIds(permResponse.data);
			const candidateCloudIdSet = new Set(candidateCloudIds);
			const filteredSites = sites.filter((site) => {
				if (!site.cloudId || !candidateCloudIdSet.has(site.cloudId)) {
					return true;
				}
				return permittedCloudIds.has(site.cloudId);
			});
			return { data: filteredSites, error: null };
		} catch {
			return { data: sites, error: null }; // Fail open
		}
	} catch (error) {
		return {
			data: [],
			error: error instanceof Error ? error.message : 'An unexpected error occurred',
		};
	}
});

/**
 * Result type returned by KnowledgeSourcesAtom.
 * On success, `data` contains the sources and `error` is null.
 * On failure, `data` is an empty array and `error` holds the message.
 */
export type AppConnector = {
	/**
	 * Stable connector identifier as returned by
	 * `/agents/configuration/knowledge` (e.g. `google_drive`,
	 * `microsoft_sharepoint`, `confluence-dc`, `jira_dc`). This is the
	 * canonical lookup key for description metadata
	 * (see `getConnectorDescription`) and the icon URL slug
	 * (`/gateway/api/assist/rovo/v1/resources/icon/{source}`).
	 */
	source: string;
	displayName: string;
	iconUrl: string;
	outboundAuthUrl: string;
	isUserOAuthed: boolean;
	/** Present when the connector came from the MCP-only path (demo or live merge). */
	type?: 'mcp' | 'connector';
};

export type AppConnectorResults = {
	data: AppConnector[];
	error: string | null;
};

/**
 * Derived async atom that fetches knowledge sources based on the current
 * cloud ID. The data is sourced from the rovo
 * `/gateway/api/assist/rovo/v1/agents/configuration/knowledge` REST endpoint
 * via `requestKnowledgeSources`, which is treated as the source of truth
 * for the connector list as well as their display metadata (name, icon,
 * outbound auth URL, OAuth state).
 *
 * Because it reads from `cloudIdAtom`, Jotai will automatically re-fetch
 * whenever it changes (e.g. when the user switches tenants).
 *
 * Never throws — on failure, returns `{ data: [], error: "..." }` so
 * consumers can handle the error gracefully without an ErrorBoundary.
 *
 * Components using this atom will suspend (React Suspense) until
 * the data is resolved. Wrap the consuming component in a
 * `<Suspense fallback={...}>` boundary.
 */
export const KnowledgeSourcesAtom: Atom<Promise<AppConnectorResults>> = atom<
	Promise<AppConnectorResults>
>(async (get) => {
	const cloudId = get(cloudIdAtom);

	if (!cloudId) {
		return { data: [], error: null };
	}

	try {
		const rovoRequest = requestKnowledgeSources(cloudId);
		const rovoResponse = await withRequestTiming(
			'operational.appConnectorsRequest.completed.twgMicrositeAppConnectors',
			rovoRequest.execute,
			{ requestName: 'knowledgeSources' },
		);

		if (!rovoResponse.success) {
			return {
				data: [],
				error: `Failed to fetch connectors: ${rovoResponse.error?.message ?? rovoResponse.error}`,
			};
		}

		const rovoConnectors = rovoResponse.data.knowledgeSources;

		const finalParsedConnectors: AppConnector[] = [];
		const seenConnectorKeys = new Set<string>();

		rovoConnectors.forEach((connector) => {
			const parsedConnector: AppConnector = {
				source: connector.source,
				displayName: connector.friendlyName,
				iconUrl: connector.iconUrl,
				outboundAuthUrl: connector.outboundAuthUrl,
				isUserOAuthed: connector.isUserOAuthed,
			};

			const connectorKey = `${parsedConnector.source}::${parsedConnector.displayName}::${
				parsedConnector.iconUrl
			}::${parsedConnector.outboundAuthUrl ?? ''}`;
			if (seenConnectorKeys.has(connectorKey)) {
				return;
			}

			seenConnectorKeys.add(connectorKey);
			finalParsedConnectors.push(parsedConnector);
		});

		return {
			data: finalParsedConnectors,
			error: null,
		};
	} catch (error) {
		return {
			data: [],
			error: error instanceof Error ? error.message : 'An unexpected error occurred',
		};
	}
});

/**
 * Result type returned by CollaborationUsersAtom.
 * On success, `data` contains the recommended collaboration users and
 * `error` is null. On failure, `data` is an empty array and `error`
 * holds the message so consumers can fall back gracefully.
 */
export type CollaborationUsersResult = {
	data: CollabGraphRecommendedEntity[];
	error: string | null;
};

/**
 * Derived async atom that fetches the user's top collaboration users
 * based on the current cloud ID and account ID.
 *
 * Because it reads from `cloudIdAtom` and `accountIdAtom`, Jotai will
 * evaluate this atom at the same time as `KnowledgeSourcesAtom` (both
 * are kicked off when their consuming components first mount under the
 * shared Suspense boundary in `Home`). It will also automatically
 * re-fetch whenever either dependency changes.
 *
 * If the cloud ID or account ID is not yet available, returns an empty
 * result without hitting the network.
 *
 * Debug panel override: when `orgMetricsSourceAtom` is `'static'` and a
 * dataset has been picked via `selectedStaticTier2DatasetKeyAtom`, the network
 * call is short-circuited and the shared `STATIC_COLLABORATION_USERS`
 * roster is returned instead — the same five users for every dataset.
 *
 * Never throws — on failure, returns `{ data: [], error: "..." }` so
 * consumers can handle the error gracefully without an ErrorBoundary.
 *
 * Components using this atom will suspend (React Suspense) until the
 * data is resolved. Wrap the consuming component in a
 * `<Suspense fallback={...}>` boundary.
 */
export const CollaborationUsersAtom: Atom<Promise<CollaborationUsersResult>> = atom<
	Promise<CollaborationUsersResult>
>(async (get) => {
	const source = get(orgMetricsSourceAtom);

	// Debug panel override: when a static dataset has been picked for the
	// active tier, serve the canned collaborator roster directly without
	// touching the network. The same roster is returned for every
	// dataset (and every tier) so demo flows always show a recognisable
	// Teamwork Graph team. Tier 3 is a pure capability showcase that
	// doesn't render collaborators, so the roster is still returned but
	// ignored downstream.
	if (source === 'static') {
		const tier = get(selectedDebugTierAtom);
		const hasSelection =
			(tier === 'tier1' && get(selectedStaticTier1DatasetKeyAtom) !== null) ||
			(tier === 'tier2' && get(selectedStaticTier2DatasetKeyAtom) !== null) ||
			(tier === 'tier3' && get(selectedStaticTier3DatasetKeyAtom) !== null);
		if (hasSelection) {
			return { data: [...STATIC_COLLABORATION_USERS], error: null };
		}
		return { data: [], error: null };
	}

	const cloudId = get(cloudIdAtom);
	const accountId = get(accountIdAtom);

	if (!cloudId || !accountId) {
		return { data: [], error: null };
	}

	try {
		const { execute } = requestCollaborationUsers({
			requestingUserId: accountId,
			targetUserId: accountId,
			tenantId: cloudId,
			limit: 10,
			excludeInactive: true,
		});
		const response = await withRequestTiming(
			'operational.collaborationUsersRequest.completed.twgMicrositeCollaborationUsers',
			execute,
		);
		if (response.success) {
			const valid: CollabGraphRecommendedEntity[] = response.data.recommendedEntities.flatMap(
				(entity, entityIndex) => {
					const dropReason = getRecommendationDropReason(entity);
					if (dropReason !== null) {
						// Sentry groups events by `message`, so the reason is in
						// the message string (one stable group per reason) while
						// the upstream `traceId` rides along as an extra/tag for
						// per-event pivoting.
						logMessage(`CollabGraph recommendation dropped: ${dropReason}`, {
							loggerName: 'collaborationUsers',
							traceId: response.traceId,
							entityIndex,
							entityId: entity?.id ?? null,
						});
						return [];
					}
					const warning = getRecommendationWarning(entity);
					if (warning !== null) {
						// Degraded but still renderable — keep the entity in
						// `valid` so the row renders (with name + the existing
						// "no shared work" fallback phrase) and emit a separate
						// `degraded:` event so we can monitor upstream quality
						// without crashing.
						logMessage(`CollabGraph recommendation degraded: ${warning}`, {
							loggerName: 'collaborationUsers',
							traceId: response.traceId,
							entityIndex,
							entityId: entity.id,
						});
					}

					if ((entity.expand.account_status ?? 'active') !== 'active') {
						// `account_status` is missing for some upstream payloads,
						// so the `?? 'active'` default keeps those rows visible
						// (treated as active). Only an explicit non-`'active'`
						// status (e.g. `'inactive'` / `'closed'`) is dropped here.
						return [];
					}

					return [entity];
				},
			);
			return { data: valid, error: null };
		}
		return {
			data: [],
			error: `Failed to fetch collaboration users: ${response.error?.message ?? response.error}`,
		};
	} catch (error) {
		return {
			data: [],
			error: error instanceof Error ? error.message : 'An unexpected error occurred',
		};
	}
});

/**
 * Result type returned by GlobalMetricsAtom.
 * On success, `data` contains the platform-wide metrics row and `error` is null.
 * On failure, `data` is `null` and `error` holds the message so consumers can
 * fall back to the existing hard-coded placeholder strings.
 */
export type GlobalMetricsResult = {
	data: GlobalMetricsResponse | null;
	error: string | null;
};

/**
 * Derived async atom that fetches the unauthenticated platform-wide hero
 * metrics from `/twg-api/v1/microsite/metrics/global`.
 *
 * The endpoint is anonymous and edge-cached at the CDN for 15 minutes, so this
 * atom has no React/Jotai dependencies and only fires once per page load. It
 * is consumed by `Stats` (rendered in the logged-out home view) under the
 * Suspense boundary in `LoggedOutView`.
 *
 * Never throws — on failure, returns `{ data: null, error: "..." }` so
 * consumers can fall back gracefully without an ErrorBoundary.
 */
export const GlobalMetricsAtom: Atom<Promise<GlobalMetricsResult>> = atom<
	Promise<GlobalMetricsResult>
>(async () => {
	try {
		const { execute } = requestGlobalMetrics();
		const response = await withRequestTiming(
			'operational.globalMetricsRequest.completed.twgMicrositeGlobalMetrics',
			execute,
		);
		if (response.success) {
			return { data: response.data, error: null };
		}
		return {
			data: null,
			error: `Failed to fetch global metrics: ${response.error?.message ?? response.error}`,
		};
	} catch (error) {
		return {
			data: null,
			error: error instanceof Error ? error.message : 'An unexpected error occurred',
		};
	}
});

/**
 * Result type returned by OrgMetricsAtom.
 *
 * On success, `data` contains either the full org-scoped metrics payload or a
 * `NoDataResponse` sentinel when the upstream dbt model has not yet produced
 * rows for the org; `error` is null in both cases. On failure, `data` is
 * `null` and `error` holds the message so consumers can fall back to the
 * existing hard-coded placeholder strings.
 *
 * Consumers should narrow on `data.status === 'NO_DATA'` to render an
 * empty-state separately from the populated metrics view.
 */
export type OrgMetricsResult = {
	data: OrgMetricsResponse | NoDataResponse | null;
	error: string | null;
};

/**
 * Derived async atom that fetches the authenticated org-scoped hero metrics
 * from `/twg-api/v1/microsite/metrics/org/{orgId}`.
 *
 * Because it reads from `orgIdAtom` and `cloudIdAtom`, Jotai will
 * automatically re-fetch whenever either dependency changes (e.g. when the
 * user switches tenants). It is consumed by the logged-in home view under a
 * shared Suspense boundary alongside `KnowledgeSourcesAtom` and
 * `CollaborationUsersAtom`.
 *
 * If the org ID or cloud ID is not yet available, returns an empty result
 * without hitting the network.
 *
 * Never throws — on failure, returns `{ data: null, error: "..." }` so
 * consumers can fall back gracefully without an ErrorBoundary.
 */
export const OrgMetricsAtom: Atom<Promise<OrgMetricsResult>> = atom<Promise<OrgMetricsResult>>(
	async (get) => {
		const source = get(orgMetricsSourceAtom);

		// Debug panel override: when the source is `'static'` and a Tier 2
		// dataset has been picked, serve it directly without touching the
		// network. The payload conforms to `OrgMetricsResponse`, so
		// downstream consumers (e.g. `selectMetrics`) treat it identically
		// to a live response. Tier 1 and Tier 3 previews intentionally do
		// not seed org metrics — Tier 1 drives `UserMetricsAtom` instead,
		// and Tier 3 is a pure capability showcase that ignores numeric
		// data — so we fall through to the null payload in those cases.
		if (source === 'static') {
			const tier = get(selectedDebugTierAtom);

			if (tier === 'tier2') {
				const datasetKey = get(selectedStaticTier2DatasetKeyAtom);
				if (datasetKey) {
					return {
						data: STATIC_TIER2_DATASETS[datasetKey].metrics,
						error: null,
					};
				}
			}
			if (tier === 'tier3') {
				const datasetKey = get(selectedStaticTier3DatasetKeyAtom);
				if (datasetKey) {
					return {
						data: STATIC_TIER3_DATASETS[datasetKey].metrics,
						error: null,
					};
				}
			}

			return { data: null, error: null };
		}

		const orgId = get(orgIdAtom);
		const cloudId = get(cloudIdAtom);
		const accountId = get(accountIdAtom);

		if (!orgId || !cloudId) {
			return { data: null, error: null };
		}

		try {
			const { execute } = requestOrgMetrics(orgId, cloudId, accountId);
			const response = await withRequestTiming(
				'operational.orgMetricsRequest.completed.twgMicrositeOrgMetrics',
				execute,
			);
			if (response.success) {
				if ('status' in response.data && response.data.status === 'NO_DATA') {
					return { data: null, error: null };
				}

				return { data: response.data, error: null };
			}
			return {
				data: null,
				error: `Failed to fetch org metrics: ${response.error?.message ?? response.error}`,
			};
		} catch (error) {
			return {
				data: null,
				error: error instanceof Error ? error.message : 'An unexpected error occurred',
			};
		}
	},
);

/**
 * Tracks which install-instructions tab is currently selected in the
 * Getting Started section ('cli' or 'mcp'). Hoisted to a module-level
 * jotai atom (instead of `useState` inside `GettingStartedSection`) so
 * external triggers — most notably the "CLI" nav button in
 * `NavButtons` — can both switch the tab *and* smooth-scroll the user
 * down to the section in the same click.
 *
 * Defaults to `'cli'` to match the previous local-state default.
 */
export const gettingStartedSelectedTabAtom: PrimitiveAtom<GettingStartedTab> & {
	init: GettingStartedTab;
} = atom<GettingStartedTab>('cli');

/**
 * Result type returned by `UserMetricsAtom` (and its split fast/slow
 * variants).
 *
 * `data` is the user-scoped metrics payload on success, or null on
 * NO_DATA (404), missing orgId, or static source. `error` is non-null
 * only on network/parse failures.
 *
 * For the split atoms:
 * - `UserMetricsFastAtom` resolves with `data.workByCollaborators` always
 *   `undefined` (the slow batch is excluded server-side via
 *   `?groups=fast,medium`).
 * - `UserMetricsSlowAtom` resolves with a sparse payload that only
 *   guarantees `workByCollaborators`; sibling fields will be present in
 *   the shape but should not be relied on by consumers — read fast
 *   fields from the merged `UserMetricsAtom` or `UserMetricsFastAtom`
 *   directly.
 */
export type UserMetricsResult = {
	data: UserMicrositeHeroMetricsResponse | null;
	error: string | null;
};

/**
 * Internal helper — issue a user-metrics request for the given groups
 * and normalise to a `UserMetricsResult`. NO_DATA / 404 / network
 * failures are funnelled through the same null-data branch so callers
 * never have to throw-catch.
 *
 * Both the fast and slow paths reuse the existing
 * `…twgMicrositeUserMetrics` timing event key — finer-grained
 * fast/slow analytics would require adding new keys to
 * `analytics.spec.yaml` and regenerating the union, which is out of
 * scope for this change.
 */
const fetchUserMetricsForGroups = async (
	orgId: string,
	cloudId: string,
	topCollaboratorAris: ReadonlyArray<string>,
	groups: ReadonlyArray<'fast' | 'medium' | 'slow'>,
): Promise<UserMetricsResult> => {
	try {
		const { execute } = requestUserMetrics(orgId, cloudId, topCollaboratorAris, groups);
		const response = await withRequestTiming(
			'operational.userMetricsRequest.completed.twgMicrositeUserMetrics',
			execute,
		);
		if (response.success) {
			if (isUserNoDataResponse(response.data)) {
				return { data: null, error: null };
			}
			return { data: response.data as UserMicrositeHeroMetricsResponse, error: null };
		}
		return {
			data: null,
			error: `Failed to fetch user metrics: ${response.error?.message ?? response.error}`,
		};
	} catch (error) {
		return {
			data: null,
			error: error instanceof Error ? error.message : 'An unexpected error occurred',
		};
	}
};

/**
 * Fast/medium derived async atom — fetches the cheap user-scoped batches
 * (`relationshipsByProvider` + `peopleWorkedWithCount`) via
 * `?groups=fast,medium`.
 *
 * Does **not** wait on `CollaborationUsersAtom` (the slow batch is
 * excluded, so the `X-Top-Collaborators` header is unnecessary). This
 * keeps time-to-first-meaningful-paint on the hero strip independent
 * of the collaboration-graph round-trip.
 *
 * Static (debug) mode short-circuits the network call with the same
 * canned Tier 1 payload as the legacy `UserMetricsAtom` — the static
 * fixtures already include both fast and slow fields, so consumers see
 * the same shape regardless of which atom they read.
 */
export const UserMetricsFastAtom: Atom<Promise<UserMetricsResult>> = atom<
	Promise<UserMetricsResult>
>(async (get) => {
	const source = get(orgMetricsSourceAtom);

	if (source === 'static') {
		const tier = get(selectedDebugTierAtom);
		if (tier === 'tier1') {
			const tier1Key = get(selectedStaticTier1DatasetKeyAtom);
			if (tier1Key) {
				return { data: STATIC_TIER1_DATASETS[tier1Key].metrics, error: null };
			}
		}
		return { data: null, error: null };
	}

	const orgId = get(orgIdAtom);
	const cloudId = get(cloudIdAtom);

	if (!orgId || !cloudId) {
		return { data: null, error: null };
	}

	return fetchUserMetricsForGroups(orgId, cloudId, [], ['fast', 'medium']);
});

/**
 * Slow derived async atom — fetches only `workByCollaborators` via
 * `?groups=slow`. Awaits `CollaborationUsersAtom` so the resolved
 * collaborator ARIs can be forwarded as the `X-Top-Collaborators`
 * header (without it, the BE omits the field entirely per the
 * integration guide).
 *
 * Decoupled from the fast atom so the (10-cypher-query plus hydration)
 * round-trip never blocks the hero strip's number rendering.
 *
 * Static (debug) mode short-circuits to the same canned Tier 1 payload
 * as `UserMetricsFastAtom`.
 */
export const UserMetricsSlowAtom: Atom<Promise<UserMetricsResult>> = atom<
	Promise<UserMetricsResult>
>(async (get) => {
	const source = get(orgMetricsSourceAtom);

	if (source === 'static') {
		const tier = get(selectedDebugTierAtom);

		if (tier === 'tier1') {
			const tier1Key = get(selectedStaticTier1DatasetKeyAtom);
			if (tier1Key) {
				return { data: STATIC_TIER1_DATASETS[tier1Key].metrics, error: null };
			}
		}

		if (tier === 'tier3') {
			const tier3Key = get(selectedStaticTier3DatasetKeyAtom);
			if (tier3Key) {
				return { data: TIER_3_USER_DATASET.metrics, error: null };
			}
		}

		return { data: null, error: null };
	}

	const orgId = get(orgIdAtom);
	const cloudId = get(cloudIdAtom);

	if (!orgId || !cloudId) {
		return { data: null, error: null };
	}

	// Wait for the collaboration-graph fetch to resolve so we can pass
	// the resolved ARIs as `X-Top-Collaborators`. We tolerate empty /
	// failed collaborator results by still firing the request without
	// the header — the BE will simply omit `workByCollaborators` from
	// its response in that case.
	const collaborationUsers = await get(CollaborationUsersAtom);
	const topCollaboratorAris = collaborationUsers.data
		.map((entity) => entity.expand?.ari)
		.filter((ari): ari is string => typeof ari === 'string' && ari.length > 0);

	return fetchUserMetricsForGroups(orgId, cloudId, topCollaboratorAris, ['slow']);
});

/**
 * Merged async atom — combines `UserMetricsFastAtom` and
 * `UserMetricsSlowAtom` into a single payload for legacy consumers
 * that still expect the full `UserMicrositeHeroMetricsResponse` in one
 * place.
 *
 * Per the integration guide, the recommended FE pattern is to fire
 * `?groups=fast,medium` first and `?groups=slow` in parallel, then
 * merge client-side. This atom encodes that merge:
 *
 * - Fields from the fast payload are taken as the base.
 * - `workByCollaborators` is taken from the slow payload (if present)
 *   and slotted in.
 * - `meta.batchOutcomes` keeps each batch's own outcome.
 * - `computedAt` resolves to the more recent of the two responses
 *   (string timestamps compare lexically because ISO-8601).
 * - `error` is the slow error if the fast call succeeded; otherwise
 *   the fast error wins (consumers care most about whether the cheap
 *   fields rendered).
 *
 * New code should prefer reading directly from `UserMetricsFastAtom` /
 * `UserMetricsSlowAtom` so the section can render without waiting on
 * the slow batch — this merged atom is kept primarily for backward
 * compatibility.
 */
export const UserMetricsAtom: Atom<Promise<UserMetricsResult>> = atom<Promise<UserMetricsResult>>(
	async (get) => {
		const [fast, slow] = await Promise.all([get(UserMetricsFastAtom), get(UserMetricsSlowAtom)]);

		// If neither call produced data, surface the fast error first
		// (it's the cheap one consumers care about most) and fall back
		// to the slow error.
		if (!fast.data && !slow.data) {
			return { data: null, error: fast.error ?? slow.error };
		}

		const base = fast.data ?? slow.data;
		// `base` is non-null here because at least one of the two has
		// data — TS narrowing.
		if (!base) {
			return { data: null, error: fast.error ?? slow.error };
		}

		const merged: UserMicrositeHeroMetricsResponse = {
			...base,
			...fast.data,
			...(slow.data ? { workByCollaborators: slow.data.workByCollaborators } : {}),
			computedAt:
				fast.data && slow.data
					? fast.data.computedAt > slow.data.computedAt
						? fast.data.computedAt
						: slow.data.computedAt
					: (fast.data ?? slow.data!).computedAt,
		};

		return { data: merged, error: fast.error ?? slow.error };
	},
);
